import ApplicationController from "application_controller.js";
import { formValidation } from "@form-validation/bundle/popular";
import { Bootstrap5 } from "@form-validation/plugin-bootstrap5";
import { Trigger } from "@form-validation/plugin-trigger";
import { AutoFocus } from "@form-validation/plugin-auto-focus";
import { useI18n } from "mixins";
import translations from "../../../locales/view_components/formvalidation-booking.json";

export default class extends ApplicationController  {
  debug = false;

  static targets = [ "mobile", "mobile_valid", "mobile_type" ];

  initialize() {
    this.log_function("initialize");

    this.datevalidations = 0;

    useI18n({
      controller: this,
      translations: translations,
      scope: "activemodel.errors.models.temporary/booking_form.attributes",
    });

    this.boundFormSubmit = this.formSubmit.bind(this);
    this.element.addEventListener("submit", this.boundFormSubmit);

    this.instance = formValidation(this.element, {
      fields: {
        "booking[first_name]": {
          validators: {
            notEmpty: {
              message: this.t("first_name.blank"),
            },
          },
        },
        "booking[last_name]": {
          validators: {
            notEmpty: {
              message: this.t("last_name.blank"),
            },
          },
        },
        "booking[email]": {
          validators: {
            emailAddress: {
              message: this.t("email.invalid"),
              requireGlobalDomain: true,
            },
            notEmpty: {
              message: this.t("email.blank"),
            },
          },
        },
        "booking[intl_tel_input]": {
          validators: {
            callback: {
              callback: () => this.numberValidator(),
            },
          },
        },
        "booking[adults]": {
          validators: {
            notEmpty: {
              message: this.t("adults.blank"),
            },
            greaterThan: {
              message: this.t("adults.greater_than_or_equal_to"),
              min: 1,
            },
          },
        },
        "booking[children]": {
          validators: {
            notEmpty: {
              message: this.t("children.blank"),
            },
            greaterThan: {
              message: this.t("children.greater_than_or_equal_to"),
              min: 0,
            },
          },
        },
        "booking[babies]": {
          validators: {
            notEmpty: {
              message: this.t("babies.blank"),
            },
            greaterThan: {
              message: this.t("babies.greater_than_or_equal_to"),
              min: 0,
            },
          },
        },
        "booking[arrival]": {
          validators: {
            notEmpty: {
              message: this.t("arrival.blank"),
            },
          },
        },
        "booking[departure]": {
          validators: {
            notEmpty: {
              message: this.t("departure.blank"),
            },
          },
        },
        "booking[nights]": {
          validators: {
            notEmpty: {
              message: this.t("nights.blank"),
            },
          },
        },
        "booking[message]": {
          validators: {
            stringLength: {
              message: this.t("message.too_long", { count: 1000 }),
              max: 1000,
            },
          },
        },
        "booking[terms]": {
          validators: {
            notEmpty: {
              message: this.t("terms.blank"),
            },
          },
        },

      },
      plugins: {
        trigger: new Trigger({
          event: "blur",
        }),
        bootstrap: new Bootstrap5({
          rowSelector: ".form-group",
        }),
        autoFocus: new AutoFocus(),
      },
    });

  }

  formSubmit(event) {
    this.log_function("formSubmit");

    //* Using a custom submit event in order for turbo to use the correct submit handler rather than the formvalidation.io submit events
    //* See this link for details: https://discuss.hotwired.dev/t/triggering-turbo-frame-with-js/1622/15

    this.instance.validate()
    .then((status) => {
      if(status != "Valid"){
        event.preventDefault();
      }
      return status;
    })
    .catch((error) => {
      this.log_error_function("formSubmit", error);
    });
  }

  revalidateDate(){
    this.log_function("revalidateDate");

    if(this.datevalidations > 0){
      this.instance.revalidateField("booking[arrival]");
      this.instance.revalidateField("booking[departure]");
      this.instance.revalidateField("booking[nights]");
    }
    this.datevalidations++;
  }

  numberValidator(){
    this.log_function("numberValidator");

    const error = this.mobile_validTarget.value.toString();
    const value = this.mobileTarget.value.toString();

    //* If there is no value just send a empty error
    if(value === "") {
      return { valid: false, message: this.t("mobile.invalid") };
    }


    //* IS_POSSIBLE: 0,
    //* INVALID_COUNTRY_CODE: 1,
    //* TOO_SHORT: 2,
    //* TOO_LONG: 3,
    //* IS_POSSIBLE_LOCAL_ONLY: 4,
    //* INVALID_LENGTH: 5,

    //* If the number is valid (0) or an uncaught exception
    //* Check if it is a mobile number and get the coresponding message
    switch (error) {
      case "0": return this.isMobileMessage;
      case "1": return { valid: false,  message: this.t("mobile.valid_country") };
      case "2": return { valid: false,  message: this.t("mobile.min_length")    };
      case "3": return { valid: false,  message: this.t("mobile.max_length")    };
      case "4": return { valid: false,  message: this.t("mobile.not_local")     };
      case "5": return { valid: false,  message: this.t("mobile.length")        };
      default:  return this.isMobileMessage;
    }
  }

  disconnect(){
    this.log_function("disconnect");

    this.instance.deregisterPlugin("___frameworkMessage"); // HACK Added this to resolve a bug
    this.instance.destroy();
    this.element.removeEventListener("submit", this.boundFormSubmit);
  }

  get isMobileMessage(){
    this.log_function("get isMobileMessage");

    //* "UNKNOWN": -1
    //* "FIXED_LINE": 0,
    //* "MOBILE": 1,
    //* "FIXED_LINE_OR_MOBILE": 2,
    //* "TOLL_FREE": 3,
    //* "PREMIUM_RATE": 4,
    //* "SHARED_COST": 5,
    //* "VOIP": 6,
    //* "PERSONAL_NUMBER": 7,
    //* "PAGER": 8,
    //* "UAN": 9,
    //* "VOICEMAIL": 10,

    const type = this.mobile_typeTarget.value.toString();
    const invalidTypes = [ "-1", "0", "3", "4", "5", "8", "9", "10" ];

    if(invalidTypes.includes(type)) {
      return { valid: false, message: this.t("mobile.mobile") };
    } else {
      return { valid: true, message: "" };
    }
  }
}
